import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa'; 

const CorePage = () => {
  const [cores, setCores] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formValues, setFormValues] = useState({ name: '', number_of_head: '' });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10); 
  // const limit = 10;

  // Debounce search query - only trigger when 3+ characters or empty
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 3 || searchQuery.length === 0) {
        setDebouncedSearchQuery(searchQuery);
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchCores(currentPage, debouncedSearchQuery);
  }, [currentPage, debouncedSearchQuery]);

  const fetchCores = async (page = 1, search = '') => {
    try {
      const res = await axios.get(`/admin/api/core-teams?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`);
      const { coreTeams = [], totalCount = 0 } = res.data;
      setCores(coreTeams);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      console.error('Failed to fetch core teams:', err);
    }
  };

 const handleSave = async () => {
  const validationErrors = validateForm(formValues, {
    name: { required: true },
    number_of_head: { required: true, type: 'number' },
  });

  if (Object.keys(validationErrors).length > 0) {
    setErrors(validationErrors);
    return;
  }

  // Ensure number_of_head is converted to number
  const formDataToSend = {
    ...formValues,
    number_of_head: parseInt(formValues.number_of_head, 10)
  };

  try {
    if (editingId) {
      await axios.put(`/admin/api/core-teams/${editingId}`, formDataToSend);
      toastSuccess('Core Team updated successfully!');
    } else {
      await axios.post('/admin/api/core-teams', formDataToSend);
      toastSuccess('Core Team added successfully!');
    }

    setEditingId(null);
    setCurrentPage(1);
    fetchCores(1, searchQuery);
    handleCancel();
  } catch (err) {
    console.error('Failed to save core team:', err);
    toastError('Failed to save core team');
  }
};

const handleEdit = (core) => {
  console.log(core); // check if _id or id
  setFormValues({ 
    name: core.name, 
    number_of_head: core.number_of_head ? core.number_of_head.toString() : '' 
  });
  setEditingId(core._id); // or core.id
  setShowModal(true);
};

  const handleDelete = async (id) => {
    const result = await confirmDelete('Are you sure you want to delete this Core Team?');
    if (result?.isConfirmed) {
      try {
        await axios.delete(`/admin/api/core-teams/${id}`);
        toastSuccess('Core Team deleted successfully!');
        setCurrentPage(1);
        fetchCores(1, searchQuery);
      } catch (err) {
        console.error('Failed to delete core team:', err);
        toastError('Failed to delete core team');
      }
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ name: '', number_of_head: '' });
    setEditingId(null);
    setErrors({});
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0"> Core Team</h3>
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search Core Team (min 3 chars)..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
        />
      </div>
   
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">Core Team List</h5>
          <button
            className="btn btn-sm btn-success"
            onClick={() => setShowModal(true)}
          >
            + Add New Core Team
          </button>
        </div>
       <div className="table-responsive">
        <div className="card-body p-0">
          <table
            className="table table-bordered table-sm mb-0"
          >
            <thead className="table-light">
              <tr>
                <th className="py-1 px-2 w-5">Sr no.</th>
                <th className="py-1 px-2 w-50">Core Team Name</th>
                <th className="py-1 px-2 w-25">Number of Head</th>
                <th className="py-1 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cores.length > 0 ? (
                cores.map((core, idx) => (
                  <tr key={core._id}>
                    <td className="py-1 px-2">{(currentPage - 1) * limit + idx + 1}</td>
                    <td className="py-1 px-2">{core.name}</td>
                    <td className="py-1 px-2">{core.number_of_head || 0}</td>
                    <td className="py-1 px-2">
                      <button
                        className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
                        title="Edit"
                        onClick={() => handleEdit(core)}
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0 text-danger"
                        onClick={() => handleDelete(core._id)}
                        title="Delete"
                      >
                        <FaTrash size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-2">No core teams found.</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>
              {/* Pagination numbers with ellipsis */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page =>
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                )
                .reduce((acc, page, idx, arr) => {
                  if (idx > 0 && page - arr[idx - 1] > 1) {
                    acc.push('ellipsis');
                  }
                  acc.push(page);
                  return acc;
                }, [])
                .map((page, idx) =>
                  page === 'ellipsis' ? (
                    <span key={`ellipsis-${idx}`} className="px-1">...</span>
                  ) : (
                    <button
                      key={page}
                      className={`btn btn-sm page-btn${currentPage === page ? ' active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  )
                )}
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit Core Team' : 'Add Core Team'}
        fields={[
              { name: 'name', label: 'Core Team', placeholder: 'Enter Core Team Name' },
              { name: 'number_of_head', label: 'Number of Head', placeholder: 'Enter Number of Head', type: 'number' }
            ]}
        values={formValues}
        errors={errors}
        onChange={setFormValues}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={editingId ? 'Update' : 'Save'}
        requiredFields={['name', 'number_of_head']}
      />
    </div>
  );
};

export default CorePage;
